import React from 'react';
import styled from 'styled-components';
import {ButtonDropdownField, Detail, DropdownList, Icon, Text} from '@forecast-it/design-system';
import {FEATURE_FLAG_DESTRUCTIVE_TEXT} from './featureFlagSurface';

/**
 * Hugging container for the trigger.
 *
 * `ButtonDropdownField` wraps itself in the design system's `InfoDisplayWrapper`, whose `FlexColumn`
 * is `flex: 1`. Placed directly in the assignment table's header — a `space-between` row — that
 * wrapper grows to absorb every spare pixel, and the trigger is left sitting against the "Currently
 * assigned" title with the header's `space-between` having nothing left to distribute. Holding the
 * control at content width is what lets the header place it at the right edge of the card.
 *
 * Lives here rather than around the header slot in the table, because the growing wrapper comes from
 * this component's own choice of trigger — a consumer should not have to know about it.
 *
 * Sits OUTSIDE the dropdown, not between the trigger and the field. The trigger renders `asChild` and
 * hands its props and ref to its single child, so a wrapper placed inside it would become the trigger
 * itself: the menu would open from a div that cannot be tabbed to, and the open-state attribute that
 * rotates the chevron would land on the wrapper instead of the field. The dropdown root renders no DOM
 * element of its own, so wrapping the whole thing costs nothing.
 */
const TriggerWrapper = styled.div`
	display: inline-flex;
	flex: 0 0 auto;
`;

// The label sits beside the icon inside the trigger, so the two need a row of their own; the field
// supplies its own trailing chevron around whatever it is given.
const TriggerContent = styled.span`
	display: flex;
	align-items: center;
	gap: 8px;
	white-space: nowrap;
`;

// Each entry is a title with a line of explanation under it, so the item's content is a column
// rather than the single line the dropdown's own items normally hold.
const MenuEntry = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	text-align: left;
`;

/**
 * The title of the entry that clears every assignment.
 *
 * Coloured here rather than through a typography prop: the typography components paint their own
 * colour onto the element they render, so a colour inherited from a wrapper would lose to it.
 */
const DestructiveEntry = styled(MenuEntry)`
	> :first-child {
		color: ${FEATURE_FLAG_DESTRUCTIVE_TEXT};
	}
`;

export interface FeatureFlagRemoveMenuProps {
	/** Start picking companies from the table below. */
	onBulkRemove: () => void;
	/** Ask to clear every assignment on the flag. Confirmed elsewhere — this only raises the question. */
	onRemoveAll: () => void;
}

/**
 * The assignment table's "Remove companies" menu.
 *
 * Both entries only ever *begin* a removal: one switches the table into selection mode, the other
 * opens a confirmation. Nothing is removed by opening this menu or by choosing from it, which is why
 * neither entry needs a confirmation of its own here.
 */
export function FeatureFlagRemoveMenu({onBulkRemove, onRemoveAll}: FeatureFlagRemoveMenuProps): JSX.Element {
	return (
		<TriggerWrapper>
			<DropdownList>
				<DropdownList.Trigger>
					{/* The neutral field-style control rather than a brand-blue button: this sits in a table
					    header beside a count, and a filled button there would outweigh the page's actual
					    primary action ("Roll out to more companies"). aria-label carries the accessible name
					    because the visible label is a child of the row inside it rather than the button's own
					    text. */}
					<ButtonDropdownField aria-label="Remove companies" data-cy="feature-flag-remove-menu" width="fit-content">
						<TriggerContent>
							<Icon icon="trash" newColor="onlight" size="s" />
							<Text>Remove companies</Text>
						</TriggerContent>
					</ButtonDropdownField>
				</DropdownList.Trigger>
				<DropdownList.Content align="end" offset={4} width="260px">
					<DropdownList.Item data-cy="feature-flag-bulk-remove" onClick={onBulkRemove}>
						<MenuEntry>
							<Text>{'Bulk remove…'}</Text>
							<Detail color="medium">Pick companies from the list below</Detail>
						</MenuEntry>
					</DropdownList.Item>
					<DropdownList.Item data-cy="feature-flag-remove-all" onClick={onRemoveAll}>
						<DestructiveEntry>
							<Text>Remove all companies</Text>
							<Detail color="medium">Clears every assignment on this flag</Detail>
						</DestructiveEntry>
					</DropdownList.Item>
				</DropdownList.Content>
			</DropdownList>
		</TriggerWrapper>
	);
}
